import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  FileVideo,
  Gauge,
  Link2,
  ListChecks,
  Lock,
  MessageCircle,
  Mic,
  MonitorUp,
  ShieldCheck,
  Smartphone,
  Video,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { BrandMark } from '@/components/BrandMark';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { cn, initials } from '@/lib/utils';

const FEATURES: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: Link2,
    title: 'Link-only rooms',
    body: 'Every interview gets its own room and a private key per person. There is no directory to browse and no code to guess.',
  },
  {
    icon: FileVideo,
    title: 'Recorded automatically',
    body: 'Recording starts with the interview and the file is waiting when it ends. Nobody has to remember to press a button.',
  },
  {
    icon: ListChecks,
    title: 'A timeline you can trust',
    body: 'Who joined, when, for how long, and whether both people were ever in the room together. Answers the "did this actually happen" question.',
  },
  {
    icon: ShieldCheck,
    title: 'The host stays in control',
    body: 'Mute, remove, or end the call for everyone. Moderation runs through the server, so a tampered browser cannot fake it.',
  },
  {
    icon: Smartphone,
    title: 'Built for the phone first',
    body: 'Most candidates join on a mid-range Android over mobile data. Video drops to audio before the call does, not after.',
  },
  {
    icon: Gauge,
    title: 'Nothing to install',
    body: 'It opens in the browser the candidate already has. No app, no account, no plugin, no support ticket.',
  },
];

const STEPS = [
  {
    title: 'Schedule it',
    body: 'Your system calls the API with the two people and a time. Two private links come back.',
  },
  {
    title: 'They join',
    body: 'Both sides tap their link, check their camera and mic, and are in. No accounts anywhere.',
  },
  {
    title: 'You get the evidence',
    body: 'The recording, the attendance timeline, and a webhook the moment it is all finished.',
  },
];

/**
 * The public front door.
 *
 * Serves two audiences that arrive here for opposite reasons: a company
 * evaluating the product, and a candidate who typed the bare domain instead of
 * opening their invite. The page leads with the pitch, but the code entry is
 * reachable from the first screenful so the second group is never stranded.
 */
export function LandingPage() {
  return (
    <div className="min-h-full">
      <SiteHeader />

      <main>
        <Hero />
        <Features />
        <HowItWorks />
        <JoinSection />
      </main>

      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  return (
    <header className="border-border/60 sticky top-0 z-40 border-b backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <BrandMark />

        <nav className="flex items-center gap-1 sm:gap-2">
          <a
            href="#features"
            className="text-muted-foreground hover:text-foreground hidden rounded-lg px-3 py-2 text-sm transition-colors sm:block"
          >
            Features
          </a>
          <a
            href="#how"
            className="text-muted-foreground hover:text-foreground hidden rounded-lg px-3 py-2 text-sm transition-colors sm:block"
          >
            How it works
          </a>
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin">Admin</Link>
          </Button>
          <Button asChild size="sm">
            <a href="#join">Join a call</a>
          </Button>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    // The preview's glow is drawn outside its box and would push the page
    // 8px wider than a 360px phone, which reads as the whole site being able
    // to scroll sideways.
    <section className="mx-auto max-w-6xl overflow-hidden px-4 pt-14 pb-16 sm:px-6 sm:pt-20 sm:pb-24">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <div className="animate-in fade-in-0 slide-in-from-bottom-4 duration-700">
          <Badge variant="outline" className="mb-5 gap-2 py-1.5">
            <span className="bg-[var(--success)] size-1.5 animate-[var(--animate-pulse-dot)] rounded-full" />
            Interviews that record themselves
          </Badge>

          <h1 className="text-4xl leading-[1.05] font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl">
            Remote interviews you can{' '}
            <span className="from-primary bg-linear-to-r to-[oklch(0.72_0.15_200)] bg-clip-text text-transparent">
              actually prove
            </span>{' '}
            happened
          </h1>

          <p className="text-muted-foreground mt-5 max-w-xl text-base text-pretty sm:text-lg">
            Hiredude Meet runs the video call, records it without anyone remembering to, and hands
            back a timeline of who really turned up. Candidates just tap a link.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg">
              <a href="#join">
                Join with a code
                <ArrowRight />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="#how">See how it works</a>
            </Button>
          </div>

          <ul className="text-muted-foreground mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            {[
              { icon: Lock, label: 'Private, link-only rooms' },
              { icon: Video, label: 'Recorded with consent' },
              { icon: Smartphone, label: 'Works on any phone' },
            ].map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-1.5">
                <Icon className="size-4 shrink-0" />
                {label}
              </li>
            ))}
          </ul>
        </div>

        <CallPreview />
      </div>
    </section>
  );
}

/**
 * A still of the product. Deliberately hand-built rather than a screenshot:
 * a screenshot would go stale the first time the call UI changes, and this
 * stays crisp at every width.
 */
function CallPreview() {
  return (
    <div
      className="animate-in fade-in-0 slide-in-from-bottom-6 relative duration-1000"
      aria-hidden="true"
    >
      <div className="from-primary/25 absolute -inset-6 -z-10 rounded-[2.5rem] bg-linear-to-tr to-transparent blur-2xl" />

      <Card className="overflow-hidden p-2.5 sm:p-3">
        <div className="mb-2.5 flex items-center justify-between gap-2 px-1.5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-xs font-semibold">Senior Backend Engineer · Round 2</span>
          </div>
          <span className="flex items-center gap-1.5 rounded-full border border-[color-mix(in_oklab,var(--destructive)_40%,transparent)] bg-[color-mix(in_oklab,var(--destructive)_16%,transparent)] px-2 py-0.5 text-[10px] font-medium text-[color-mix(in_oklab,var(--destructive)_78%,white)]">
            <span className="size-1.5 animate-[var(--animate-pulse-dot)] rounded-full bg-current" />
            REC
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          {[
            { name: 'Priya Aggarwal', tone: 'from-[oklch(0.5_0.16_275)]', speaking: true },
            { name: 'Rahul Mehta', tone: 'from-[oklch(0.48_0.13_200)]', speaking: false },
          ].map((person) => (
            <div
              key={person.name}
              className={cn(
                'relative grid aspect-4/3 place-items-center overflow-hidden rounded-xl bg-linear-to-br to-[oklch(0.2_0.02_265)] ring-1',
                person.tone,
                person.speaking ? 'ring-primary/70' : 'ring-white/10',
              )}
            >
              <span className="grid size-11 place-items-center rounded-full bg-black/30 text-sm font-semibold backdrop-blur-sm sm:size-14 sm:text-base">
                {initials(person.name)}
              </span>
              <span className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] backdrop-blur-sm">
                {!person.speaking && <Mic className="size-2.5" />}
                <span className="max-w-20 truncate sm:max-w-none">{person.name}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="mt-2.5 flex items-center justify-center gap-1.5 rounded-xl border bg-white/[0.03] p-1.5">
          {[Mic, Video, MonitorUp, MessageCircle].map((Icon, index) => (
            <span
              key={index}
              className="grid size-8 place-items-center rounded-lg bg-white/[0.06] sm:size-9"
            >
              <Icon className="size-3.5 sm:size-4" />
            </span>
          ))}
          <span className="bg-destructive ml-1 grid h-8 w-11 place-items-center rounded-lg sm:h-9 sm:w-12">
            <Video className="size-3.5 sm:size-4" />
          </span>
        </div>
      </Card>
    </div>
  );
}

function Features() {
  return (
    <section id="features" className="border-border/60 scroll-mt-20 border-t">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
            Everything an interview needs, and nothing it does not
          </h2>
          <p className="text-muted-foreground mt-4 text-pretty">
            No lobby, no waiting room, no meeting IDs to read out. The call opens, it records, and
            it tells you what happened.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <Card
              key={title}
              className="hover:border-border group p-6 transition-colors duration-200"
            >
              <span className="bg-primary/12 text-primary-foreground ring-primary/25 mb-4 grid size-10 place-items-center rounded-xl ring-1">
                <Icon className="size-5" />
              </span>
              <h3 className="font-semibold tracking-tight">{title}</h3>
              <p className="text-muted-foreground mt-2 text-sm text-pretty">{body}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="border-border/60 scroll-mt-20 border-t">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
            Three steps, and only one of them is yours
          </h2>
        </div>

        <ol className="mt-12 grid gap-6 md:grid-cols-3">
          {STEPS.map((step, index) => (
            <li key={step.title} className="relative">
              <div className="flex items-center gap-3">
                <span className="bg-primary/15 text-primary-foreground ring-primary/30 grid size-9 shrink-0 place-items-center rounded-full text-sm font-semibold tabular-nums ring-1">
                  {index + 1}
                </span>
                <h3 className="font-semibold tracking-tight">{step.title}</h3>
              </div>
              <p className="text-muted-foreground mt-3 text-sm text-pretty md:pl-12">{step.body}</p>
            </li>
          ))}
        </ol>

        <Card className="mt-12 flex flex-col items-start gap-5 p-6 sm:flex-row sm:items-center sm:p-8">
          <span className="bg-[color-mix(in_oklab,var(--success)_14%,transparent)] grid size-11 shrink-0 place-items-center rounded-xl text-[var(--success)] ring-1 ring-[color-mix(in_oklab,var(--success)_30%,transparent)]">
            <CalendarClock className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold tracking-tight">Links open when the interview does</h3>
            <p className="text-muted-foreground mt-1.5 text-sm text-pretty">
              A link works from fifteen minutes before the slot until an hour after it. Nobody
              wanders into a room three days early, and nobody is locked out for being late.
            </p>
          </div>
        </Card>
      </div>
    </section>
  );
}

/**
 * The candidate's escape hatch. Kept on the marketing page rather than moved
 * to its own route: someone who mistyped the domain is already lost, and one
 * more navigation is one more chance to give up.
 */
function JoinSection() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');

  return (
    <section id="join" className="border-border/60 scroll-mt-20 border-t">
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
        <Card className="p-6 sm:p-10">
          <h2 className="text-2xl font-semibold tracking-tight text-balance sm:text-3xl">
            Here for an interview?
          </h2>
          <p className="text-muted-foreground mt-3 text-pretty">
            Open the link your recruiter sent you. It carries an access key this page cannot
            recreate, so it is the only way into your room.
          </p>

          <div className="text-muted-foreground/90 mt-6 flex items-center gap-2.5 rounded-xl border border-[color-mix(in_oklab,var(--success)_25%,transparent)] bg-[color-mix(in_oklab,var(--success)_10%,transparent)] px-3.5 py-3 text-sm">
            <MessageCircle className="size-4 shrink-0 text-[var(--success)]" />
            Check your WhatsApp for a message from your recruiter
          </div>

          <div className="my-7 flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-muted-foreground text-[11px] font-medium tracking-[0.08em] uppercase">
              or
            </span>
            <Separator className="flex-1" />
          </div>

          <form
            className="code-form space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = code.trim().toLowerCase();
              if (trimmed) navigate(`/${encodeURIComponent(trimmed)}`);
            }}
          >
            <Label htmlFor="code">Have a meeting code?</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="abc-defg-hij"
                autoComplete="off"
                spellCheck={false}
                className="font-mono tracking-tight"
              />
              <Button type="submit" disabled={!code.trim()} className="shrink-0">
                Continue
                <ArrowRight />
              </Button>
            </div>
            <p className="text-muted-foreground pt-1 text-xs">
              You will still need the key from your invite to enter the room.
            </p>
          </form>

          <ul className="text-muted-foreground mt-8 grid gap-2.5 text-sm sm:grid-cols-2">
            {[
              'Nothing to install',
              'No account needed',
              'Works on mobile data',
              'Camera and mic checked first',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <CheckCircle2 className="size-4 shrink-0 text-[var(--success)]" />
                {item}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="border-border/60 border-t">
      <div className="text-muted-foreground mx-auto flex max-w-6xl flex-col gap-4 px-4 py-8 text-sm sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <BrandMark size="sm" />
        <p>Recorded interviews for teams that hire remotely.</p>
      </div>
    </footer>
  );
}
